import { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { SummaryMetricCard, MetricGrid } from '@/components/global/SummaryMetricCard';
import { Package, AlertTriangle, Percent, TrendingUp, Download } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ProductIdentity } from '@/components/products';
import { downloadCsv } from '@/lib/csvExport';

interface MarginAnalysisItem {
  productId: string;
  productSku: string;
  productName: string;
  productImageUrl: string | null;
  categoryName: string | null;
  supplierName: string | null;
  purchasePrice: number;
  sellPrice: number;
  avgCommissionRate: number;
  grossMarginPercent: number | null;
  operatingMarginPercent: number | null;
  targetMarginPercent: number;
  meetsTarget: boolean | null;
  ragStatus: 'green' | 'amber' | 'red' | null;
  suggestedSellPrice: number | null;
}

interface MarginAnalysisReport {
  items: MarginAnalysisItem[];
  summary: {
    totalProducts: number;
    belowTargetCount: number;
    averageMargin: number | null;
    greenCount: number;
    amberCount: number;
    redCount: number;
  };
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`;
}

function RAGBadge({ status }: { status: 'green' | 'amber' | 'red' | null }) {
  if (status == null) {
    return <span className="text-sm text-muted-foreground">N/A</span>;
  }
  const colors = {
    green: 'bg-green-100 text-green-800 border-green-200',
    amber: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    red: 'bg-red-100 text-red-800 border-red-200',
  };

  return (
    <span className={cn('px-2 py-1 rounded-full text-xs font-medium border', colors[status])}>
      {status.toUpperCase()}
    </span>
  );
}

export default function MarginAnalysisPage() {
  const [report, setReport] = useState<MarginAnalysisReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [showBelowTarget, setShowBelowTarget] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch('/api/v1/reports/margin');
      if (res.ok) {
        const data = await res.json();
        setReport(data);
      }
    } catch (error) {
      console.error('Error loading margin analysis:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const displayedItems = showBelowTarget
    ? report?.items.filter((item) => item.meetsTarget !== true)
    : report?.items;

  function exportCsv() {
    if (!displayedItems || displayedItems.length === 0) return;
    downloadCsv(
      `margin-analysis-${new Date().toISOString().slice(0, 10)}.csv`,
      [
        'Product',
        'SKU',
        'Category',
        'Purchase (GBP)',
        'Sell (GBP)',
        'Commission %',
        'Operating margin %',
        'Target %',
        'Status',
        'Meets target',
        'Suggested sell (GBP)',
      ],
      displayedItems.map((item) => [
        item.productName,
        item.productSku,
        item.categoryName ?? '',
        item.purchasePrice > 0 ? item.purchasePrice.toFixed(2) : '',
        item.sellPrice.toFixed(2),
        item.avgCommissionRate.toFixed(1),
        item.operatingMarginPercent != null ? item.operatingMarginPercent.toFixed(1) : '',
        item.targetMarginPercent.toFixed(1),
        item.ragStatus?.toUpperCase() ?? 'N/A',
        item.meetsTarget == null ? '' : item.meetsTarget ? 'Yes' : 'No',
        item.suggestedSellPrice != null ? item.suggestedSellPrice.toFixed(2) : '',
      ])
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="space-y-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-semibold">Margin Analysis</h1>
          <Button
            variant="outline"
            onClick={exportCsv}
            disabled={loading || !displayedItems || displayedItems.length === 0}
          >
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>

        <MetricGrid className="mb-6">
          <SummaryMetricCard
            label="Total Products"
            value={report ? report.summary.totalProducts.toString() : '-'}
            icon={Package}
            isLoading={loading}
          />
          <SummaryMetricCard
            label="Below Target"
            value={report ? report.summary.belowTargetCount.toString() : '-'}
            icon={AlertTriangle}
            status={report && report.summary.belowTargetCount > 0 ? 'red' : undefined}
            isLoading={loading}
          />
          <SummaryMetricCard
            label="Average Margin"
            value={
              report
                ? report.summary.averageMargin != null
                  ? formatPercent(report.summary.averageMargin)
                  : 'N/A'
                : '-'
            }
            icon={Percent}
            isLoading={loading}
          />
          <SummaryMetricCard
            label="Healthy Products"
            value={report ? report.summary.greenCount.toString() : '-'}
            icon={TrendingUp}
            status="green"
            isLoading={loading}
          />
        </MetricGrid>

        <Card className="mb-4">
          <div className="p-4 flex items-center gap-4 border-b">
            <Button
              variant={showBelowTarget ? 'default' : 'outline'}
              size="sm"
              onClick={() => setShowBelowTarget(!showBelowTarget)}
            >
              {showBelowTarget ? 'Show All' : 'Show Below Target Only'}
            </Button>
            <div className="flex items-center gap-2 ml-auto">
              <span className="text-sm text-muted-foreground">RAG:</span>
              <RAGBadge status="green" />
              <span className="text-xs text-muted-foreground">{report?.summary.greenCount || 0}</span>
              <RAGBadge status="amber" />
              <span className="text-xs text-muted-foreground">{report?.summary.amberCount || 0}</span>
              <RAGBadge status="red" />
              <span className="text-xs text-muted-foreground">{report?.summary.redCount || 0}</span>
            </div>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Product</TableHead>
                <TableHead>Category</TableHead>
                <TableHead className="text-right">Purchase</TableHead>
                <TableHead className="text-right">Sell</TableHead>
                <TableHead className="text-right">Commission</TableHead>
                <TableHead className="text-right">Margin</TableHead>
                <TableHead className="text-right">Target</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Suggested</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : displayedItems?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                    {showBelowTarget ? 'All products meet target margin!' : 'No products found'}
                  </TableCell>
                </TableRow>
              ) : (
                displayedItems?.map((item) => (
                  <TableRow
                    key={item.productId}
                    className={item.meetsTarget === false ? 'bg-red-50' : undefined}
                  >
                    <TableCell>
                      <ProductIdentity
                        productId={item.productId}
                        name={item.productName}
                        sku={item.productSku}
                        imageUrl={item.productImageUrl}
                        size="sm"
                      />
                    </TableCell>
                    <TableCell>{item.categoryName || '-'}</TableCell>
                    <TableCell className="text-right">
                      {item.purchasePrice > 0 ? formatCurrency(item.purchasePrice) : 'N/A'}
                    </TableCell>
                    <TableCell className="text-right">{formatCurrency(item.sellPrice)}</TableCell>
                    <TableCell className="text-right">{formatPercent(item.avgCommissionRate)}</TableCell>
                    <TableCell className="text-right font-medium">
                      {item.operatingMarginPercent != null
                        ? formatPercent(item.operatingMarginPercent)
                        : 'N/A'}
                    </TableCell>
                    <TableCell className="text-right">{formatPercent(item.targetMarginPercent)}</TableCell>
                    <TableCell>
                      <RAGBadge status={item.ragStatus} />
                    </TableCell>
                    <TableCell className="text-right">
                      {item.suggestedSellPrice ? formatCurrency(item.suggestedSellPrice) : '-'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      </div>
    </div>
  );
}
